import { KitTouchableIcon } from '@omni/kit/components';
import Colors from '@omni/kit/theming/Colors';
import { ThemeContext } from '@omni/kit/theming/ThemeContext';
import { BibleBook, BibleLocation } from '@omni/kit/types/bible';
import { SCREEN_WIDTH } from '@omni/kit/utilities/utilities';
import React, {
  ReactElement,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';
import { FlatList, Platform, StyleSheet, View } from 'react-native';
import LinearGradient from 'react-native-linear-gradient';

import BibleNavModalItem from '../screens/BibleNavModalItem';

type Props = {
  bibleBooks: BibleBook[];
  bibleLocation?: BibleLocation;
  onSelectChapter?: (chapter: number, osis: string) => void;
  toggleModal?: () => void;
};

export default function BibleNavContent({
  bibleBooks = [],
  bibleLocation = { chapter: -1, bookName: '' },
  onSelectChapter = undefined,
  toggleModal = undefined,
}: Props): ReactElement {
  const { colorForScheme } = useContext(ThemeContext);
  const flatListRef = useRef<FlatList<BibleBook>>(null);
  const [hasScrolled, setHasScrolled] = useState(false);

  const bibleBookExpandedState = Array(bibleBooks.length).fill(false);
  const initialIndex = bibleBooks.findIndex(
    (b) => b.osis === bibleLocation.bookName
  );

  if (initialIndex >= 0) {
    bibleBookExpandedState[initialIndex] = true;
  }

  // Scroll when content is fully laid out and measured
  // workaround for https://github.com/facebook/react-native/issues/41163
  const handleContentSizeChange = useCallback(() => {
    if (!hasScrolled && initialIndex >= 0 && flatListRef.current) {
      setHasScrolled(true);
      requestIdleCallback(() => {
        flatListRef.current?.scrollToOffset({
          offset: 60 * initialIndex,
          animated: false,
        });
      });
    }
  }, [hasScrolled, initialIndex]);

  const _renderItem = ({
    index,
    item: book,
  }: {
    index: number;
    item: BibleBook;
  }) => {
    return (
      <BibleNavModalItem
        bibleLocation={bibleLocation}
        book={book}
        index={index}
        initialExpandedState={bibleBookExpandedState[index]}
        onExpanded={_onExpanded}
        selectNewChapter={_selectNewChapter}
      />
    );
  };

  // preserve expanded state for case where item is re-rendered when it is scrolled back on screen
  // otherwise the expanded state will be reset for the item
  const _onExpanded = (index: number, expanded: boolean) => {
    bibleBookExpandedState[index] = expanded;
  };

  const _selectNewChapter = (chapter: number, osis: string) => {
    onSelectChapter?.(chapter, osis);
  };

  return (
    <View
      style={{
        backgroundColor: colorForScheme?.({
          dark: Colors.N1000,
          light: Colors.N0,
        }),
        ...Platform.select({ web: { flex: 1 } }),
        // Overrides react-native-web-npm patch that sets the default zIndex to 'auto' instead of 0
        zIndex: 0,
      }}
    >
      {toggleModal ? (
        <View style={[styles.header]}>
          <KitTouchableIcon
            color={colorForScheme?.({ light: Colors.N900, dark: Colors.N0 })}
            name='close'
            onPress={toggleModal}
          />
        </View>
      ) : null}

      <View
        style={{
          zIndex: 6,
          position: 'relative',
          ...Platform.select({ web: { flex: 1 } }),
        }}
      >
        <LinearGradient
          colors={[
            colorForScheme?.({
              dark: '#000000ff',
              light: '#ffffffff',
            }) as string,
            colorForScheme?.({
              dark: '#00000000',
              light: '#ffffff00',
            }) as string,
          ]}
          style={{
            backgroundColor: '#ffffff00',
            height: 20,
            left: 0,
            position: 'absolute',
            top: 0,
            width: SCREEN_WIDTH,
            zIndex: 7,
          }}
        />
        <FlatList
          ref={flatListRef}
          data={bibleBooks}
          getItemLayout={(_data, index) => ({
            index,
            length: 60,
            offset: 60 * index,
          })}
          initialNumToRender={bibleBooks.length}
          keyExtractor={(item) => item.display}
          ListHeaderComponent={() => <View style={{ height: 20 }} />}
          onContentSizeChange={handleContentSizeChange}
          ListFooterComponent={() => (
            <View
              style={{
                width: 50,
                ...Platform.select({ native: { height: 500 } }),
              }}
            />
          )}
          renderItem={_renderItem}
          style={{ ...Platform.select({ web: { height: 500 } }) }}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  header: {
    paddingLeft: 12,
    zIndex: 1,
  },
});
